/**
 * CIP fork: every in-app help link names the upstream langfuse.com page it
 * was written for, and resolves here to a Weval Studio docs page or to
 * `null`. Callers hide the link on `null`, so Studio never sends people to
 * another product's documentation, often for features Studio hides.
 *
 * Callers pass the upstream path (e.g. "/docs/evaluation/overview#setup")
 * rather than a Studio path, so links that upstream merges add stay easy to
 * spot and map. To give a page a Studio target, add its upstream path to
 * `STUDIO_DOCS_PAGES`.
 */

/**
 * Upstream langfuse.com path (no hash, no trailing slash) → path on the
 * Studio docs site. Anything missing resolves to `null`.
 */
export const STUDIO_DOCS_PAGES: Readonly<Record<string, string>> = {
  "/docs": "/",
};

const UPSTREAM_ORIGIN = /^https?:\/\/(?:www\.)?langfuse\.com(?=\/|$)/;

export function resolveDocsUrl(
  upstreamPath: string,
  options: {
    baseUrl: string | undefined;
    pages?: Readonly<Record<string, string>>;
  },
): string | null {
  const { baseUrl, pages = STUDIO_DOCS_PAGES } = options;
  if (!baseUrl) return null;

  const withoutOrigin = upstreamPath.trim().replace(UPSTREAM_ORIGIN, "");
  // Upstream anchors name upstream headings, so they are not carried over.
  const [rawPath] = withoutOrigin.split(/[?#]/);
  const normalized = `/${rawPath.replace(/^\/+|\/+$/g, "")}`;

  if (!Object.prototype.hasOwnProperty.call(pages, normalized)) return null;
  const target = pages[normalized];

  const base = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  return new URL(target.replace(/^\/+/, ""), base).toString();
}
